// Exemplo de uso das Firebase Functions no frontend React

import { httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { functions } from './firebase';

// Conectar ao emulador apenas se variável explícita estiver definida
if (process.env.REACT_APP_USE_FUNCTIONS_EMULATOR === 'true') {
  connectFunctionsEmulator(functions, 'localhost', 5001);
}

// =====================================================
// 1. Gerar Pagamento PIX
// =====================================================

export const generatePixPayment = async (paymentData) => {
  const generatePix = httpsCallable(functions, 'generatePixPayment');
  
  try {
    const result = await generatePix(paymentData);
    return { data: result.data, error: null };
  } catch (error) {
    return { data: null, error: error.message };
  }
};

// Uso no componente:
/*
const handleGeneratePix = async () => {
  const { data, error } = await generatePixPayment({
    companyId: userData.companyId,
    reservationId: reservation.id,
    clientId: client.id,
    amount: 250.00,
    type: 'partial' // ou 'full'
  });

  if (error) {
    alert('Erro ao gerar PIX: ' + error);
    return;
  }

  // Exibir QR Code
  setPixData(data);
  setShowPixModal(true);
};
*/

// =====================================================
// 2. Verificar Disponibilidade
// =====================================================

export const checkServiceAvailability = async (availabilityData) => {
  const checkAvailability = httpsCallable(functions, 'checkAvailability');
  
  try {
    const result = await checkAvailability(availabilityData);
    return { data: result.data, error: null };
  } catch (error) {
    return { data: null, error: error.message };
  }
};

// Uso no componente:
/*
const handleCheckAvailability = async () => {
  const { data, error } = await checkServiceAvailability({
    companyId: userData.companyId,
    serviceId: selectedService.id,
    date: selectedDate,
    duration: 2 // horas
  });

  if (error) {
    alert('Erro ao verificar disponibilidade: ' + error);
    return;
  }

  if (!data.available) {
    alert(`Sem disponibilidade. Capacidade esgotada (${data.bookedCapacity}/${data.totalCapacity})`);
  } else {
    alert(`Disponível! Restam ${data.remainingCapacity} vagas`);
  }
};
*/

// =====================================================
// 3. Gerar Relatório de Comissões
// =====================================================

export const generateCommissionReport = async (reportData) => {
  const generateReport = httpsCallable(functions, 'generateCommissionReport');
  
  try {
    const result = await generateReport(reportData);
    return { data: result.data, error: null };
  } catch (error) {
    return { data: null, error: error.message };
  }
};

// Uso no componente:
/*
const handleGenerateReport = async () => {
  setLoading(true);
  
  const { data, error } = await generateCommissionReport({
    companyId: userData.companyId,
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString()
  });

  setLoading(false);

  if (error) {
    alert('Erro ao gerar relatório: ' + error);
    return;
  }

  setReportData(data);
  // data contém:
  // {
  //   totalReservations: 45,
  //   totalRevenue: 12500.00,
  //   totalCommissions: 1250.00,
  //   byService: { ... }
  // }
};
*/

// =====================================================
// 4. Criar Empresa (Root only)
// =====================================================

export const createCompany = async (companyData) => {
  const create = httpsCallable(functions, 'createCompany');
  
  try {
    const result = await create(companyData);
    return { data: result.data, error: null };
  } catch (error) {
    return { data: null, error: error.message };
  }
};

// Uso no componente:
/*
const handleCreateCompany = async () => {
  const { data, error } = await createCompany({
    name: companyName,
    ownerId: selectedUserId,
    freeDays: 14
  });

  if (error) {
    alert('Erro ao criar empresa: ' + error);
    return;
  }

  alert(`Empresa criada com sucesso! ID: ${data.companyId}`);
  navigate('/companies');
};
*/

// =====================================================
// 5. Exportar Dados da Empresa
// =====================================================

export const exportCompanyData = async (exportData) => {
  const exportFunc = httpsCallable(functions, 'exportCompanyData');
  
  try {
    const result = await exportFunc(exportData);
    return { data: result.data, error: null };
  } catch (error) {
    return { data: null, error: error.message };
  }
};

// Uso no componente:
/*
const handleExportData = async () => {
  setLoading(true);
  
  const { data, error } = await exportCompanyData({
    companyId: userData.companyId,
    collections: ['reservations', 'clients', 'payments', 'services']
  });

  setLoading(false);

  if (error) {
    alert('Erro ao exportar dados: ' + error);
    return;
  }

  // Converter para JSON e baixar
  const jsonStr = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonStr], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `sdc-turismo-export-${Date.now()}.json`;
  link.click();
};
*/

// =====================================================
// Componente de Exemplo: Gerador de PIX
// =====================================================

/*
import React, { useState } from 'react';
import { generatePixPayment } from '../services/functionsService';
import { QRCodeSVG } from 'qrcode.react';

const PixPaymentGenerator = ({ reservation, client, onClose }) => {
  const [pixData, setPixData] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleGenerate = async (type) => {
    setLoading(true);

    const amount = type === 'full' 
      ? reservation.totalAmount 
      : reservation.totalAmount * 0.5; // 50% de sinal

    const { data, error } = await generatePixPayment({
      companyId: reservation.companyId,
      reservationId: reservation.id,
      clientId: client.id,
      amount,
      type
    });

    setLoading(false);

    if (error) {
      alert('Erro ao gerar PIX: ' + error);
      return;
    }

    setPixData(data);
  };

  if (!pixData) {
    return (
      <div className="pix-generator">
        <h2>Gerar Pagamento PIX</h2>
        <p>Valor total: R$ {reservation.totalAmount.toFixed(2)}</p>
        
        <button 
          onClick={() => handleGenerate('partial')} 
          disabled={loading}
        >
          Gerar PIX - Sinal 50% (R$ {(reservation.totalAmount * 0.5).toFixed(2)})
        </button>
        
        <button 
          onClick={() => handleGenerate('full')} 
          disabled={loading}
        >
          Gerar PIX - Total (R$ {reservation.totalAmount.toFixed(2)})
        </button>
      </div>
    );
  }

  return (
    <div className="pix-display">
      <h2>PIX Gerado com Sucesso!</h2>
      
      <div className="qr-code">
        <QRCodeSVG value={pixData.pixKey} size={256} />
      </div>

      <div className="pix-info">
        <p><strong>Valor:</strong> R$ {pixData.amount.toFixed(2)}</p>
        <p><strong>Válido até:</strong> {new Date(pixData.expirationDate).toLocaleString()}</p>
        
        <div className="pix-key">
          <label>Chave PIX (Pix Copia e Cola):</label>
          <input 
            type="text" 
            value={pixData.pixKey} 
            readOnly 
          />
          <button onClick={() => navigator.clipboard.writeText(pixData.pixKey)}>
            Copiar
          </button>
        </div>
      </div>

      <button onClick={onClose}>Fechar</button>
    </div>
  );
};

export default PixPaymentGenerator;
*/
